/**
 * Иконка карточки «Избранное» (эпик 8, стори 8.2) — компас с живой стрелкой.
 *
 * <p>До этого здесь по очереди стояли эмодзи ⭐, рисованные знаки (вегвисир / роза ветров)
 * и растровый компас icons8. Растр пришлось оставить: анимировать в нём стрелку отдельно
 * от корпуса нельзя, а именно это и требовалось. Теперь компас рисуется векторно, и корпус
 * с лимбом стоит неподвижно, пока стрелка живёт.
 *
 * <p><b>Как устроено движение.</b> Стрелка не крутится равномерно — она ведёт себя как
 * настоящая магнитная: получает толчок, проскакивает север, качается всё слабее и замирает.
 * Отсюда лесенка шагов в {@link SETTLE_STEPS} с убывающими амплитудой и длительностью;
 * после неё пауза, и всё повторяется. Равномерное вращение здесь читалось бы индикатором
 * загрузки — в списке, где больше ничего не крутится, это сбивало бы с толку.
 *
 * <p><b>Почему три слоя, а не анимированный SVG.</b> Вращать нужно только стрелку, и проще
 * всего это делает обычный `Animated.View` с `transform` поверх статичной подложки: у него
 * есть нативный драйвер, и он одинаково работает в вебе и на устройстве. Анимировать
 * пропы внутри `react-native-svg` пришлось бы без нативного драйвера, кадр за кадром через
 * JS-мост. Порядок слоёв — корпус, стрелка, ось: ось перекрывает стрелку, как в железе.
 */
import React, { useEffect, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Line, Path, Stop } from 'react-native-svg';

/** Холст компаса: все координаты ниже — в этой системе, размер задаёт только `size`. */
const BOX = 100;
const C = BOX / 2;

/** Нативный драйвер для `transform` есть везде, кроме react-native-web, где он лишний. */
const USE_NATIVE_DRIVER = Platform.OS !== 'web';

/**
 * Успокоение стрелки. Первый шаг — сам толчок; дальше затухающие колебания вокруг севера.
 * Финальная пауза нужна, чтобы компас не дёргался без остановки: он живой, но не мельтешит.
 */
const SETTLE_STEPS: ReadonlyArray<{ toValue: number; duration: number; easing: (v: number) => number }> = [
  { toValue: 146, duration: 300, easing: Easing.in(Easing.quad) },
  { toValue: -34, duration: 828, easing: Easing.inOut(Easing.quad) },
  { toValue: 19, duration: 690, easing: Easing.inOut(Easing.quad) },
  { toValue: -9, duration: 598, easing: Easing.inOut(Easing.quad) },
  { toValue: 4, duration: 506, easing: Easing.inOut(Easing.quad) },
  { toValue: -1.5, duration: 414, easing: Easing.inOut(Easing.quad) },
  { toValue: 0, duration: 368, easing: Easing.out(Easing.quad) },
];
const SETTLE_REST_MS = 1196;

/**
 * Радиусы корпуса. Ободок оставляет себе четыре единицы из пятидесяти — ровно столько, чтобы
 * читаться кольцом; всё остальное отдано циферблату. Прежние 39 против 48 делали из компаса
 * серую шайбу с маленьким белым окошком: в списке и в шапке было видно ободок, а не прибор.
 */
const BEZEL_R = 48;
const FACE_R = 44;

/**
 * Стрелка. Она рисуется двумя половинами одного ромба, и обе упираются в осевую линию y = 50:
 * северная — красная, южная — светлая. Раньше половины были кусками, заходящими друг за друга
 * (север до y = 57, юг до y = 43), и светлая, ложась поверх красной, срезала ей широкую часть —
 * стрелка выглядела обрубленной у оси, будто её не дорисовали.
 */
const NEEDLE_TIP = 11;
const NEEDLE_HALF_WIDTH = 9;
const NEEDLE_NORTH = `M${C} ${NEEDLE_TIP} L${C + NEEDLE_HALF_WIDTH} ${C} L${C - NEEDLE_HALF_WIDTH} ${C} Z`;
const NEEDLE_SOUTH = `M${C} ${BOX - NEEDLE_TIP} L${C - NEEDLE_HALF_WIDTH} ${C} L${C + NEEDLE_HALF_WIDTH} ${C} Z`;

/** Штрихи лимба: восемь румбов, основные — толще. Считаются один раз на модуль. */
const TICKS = Array.from({ length: 8 }, (_, i) => {
  const a = (i * Math.PI) / 4;
  const sin = Math.sin(a);
  const cos = Math.cos(a);
  return {
    x1: C + sin * 34,
    y1: C - cos * 34,
    x2: C + sin * 40,
    y2: C - cos * 40,
    width: i % 2 === 0 ? 2.4 : 1.4,
  };
});

type SavedChatIconProps = {
  /** По умолчанию — размер аватарки в строке списка, чтобы карточка стояла вровень с чатами. */
  size?: number;
};

export function SavedChatIcon({ size = 38 }: SavedChatIconProps) {
  const [angle] = useState(() => new Animated.Value(0));
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let alive = true;
    AccessibilityInfo.isReduceMotionEnabled()
      .then((on) => {
        if (alive) setReduceMotion(on);
      })
      .catch(() => {
        /* Настройки нет — считаем, что анимация разрешена. */
      });
    // В react-native-web подписки может не быть вовсе, отсюда осторожное снятие.
    const sub = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (on: boolean) => {
      if (alive) setReduceMotion(on);
    });
    return () => {
      alive = false;
      sub?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      angle.setValue(0);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        ...SETTLE_STEPS.map((step) =>
          Animated.timing(angle, { ...step, useNativeDriver: USE_NATIVE_DRIVER }),
        ),
        Animated.delay(SETTLE_REST_MS),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      angle.setValue(0);
    };
  }, [angle, reduceMotion]);

  const rotate = angle.interpolate({
    inputRange: [-360, 360],
    outputRange: ['-360deg', '360deg'],
  });

  return (
    <View style={{ width: size, height: size }} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <Svg width={size} height={size} viewBox={`0 0 ${BOX} ${BOX}`}>
        <Defs>
          <LinearGradient id="savedCompassBezel" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#f2f4f6" />
            <Stop offset="0.5" stopColor="#c3c9cf" />
            <Stop offset="1" stopColor="#8f979f" />
          </LinearGradient>
          <LinearGradient id="savedCompassFace" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#fffdf7" />
            <Stop offset="1" stopColor="#ece5d5" />
          </LinearGradient>
        </Defs>
        <Circle cx={C} cy={C} r={BEZEL_R} fill="url(#savedCompassBezel)" />
        <Circle cx={C} cy={C} r={BEZEL_R} fill="none" stroke="#6f777f" strokeWidth={1.5} />
        <Circle cx={C} cy={C} r={FACE_R} fill="url(#savedCompassFace)" stroke="#a7a294" strokeWidth={1.5} />
        {TICKS.map((tick, i) => (
          <Line
            key={i}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke="#b9b2a2"
            strokeWidth={tick.width}
            strokeLinecap="round"
          />
        ))}
      </Svg>

      <Animated.View style={[StyleSheet.absoluteFill, { transform: [{ rotate }] }]} pointerEvents="none">
        <Svg width={size} height={size} viewBox={`0 0 ${BOX} ${BOX}`}>
          <Path d={NEEDLE_NORTH} fill="#c8382b" />
          <Path d={NEEDLE_SOUTH} fill="#e8e4da" stroke="#a7a294" strokeWidth={0.7} />
        </Svg>
      </Animated.View>

      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Svg width={size} height={size} viewBox={`0 0 ${BOX} ${BOX}`}>
          <Circle cx={C} cy={C} r={4.5} fill="#4a5057" />
          <Circle cx={C} cy={C} r={1.8} fill="#d8dce0" />
        </Svg>
      </View>
    </View>
  );
}
